import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    hasNotificationDestination,
    telegramLinkCommands
} from './notificationSettings.utils';
import userStore from '../../../../stores/user.store';
import fetchService from '../../../../servises/fetch.service';
import MenuSettingsComponent from '../../../components/menuSettings/MenuSettings.component';
import {
    EuiSwitch,
    EuiSpacer,
    EuiFieldText, 
    EuiFormRow, 
    EuiButton, 
    EuiCodeBlock, 
    EuiCopy
} from '@elastic/eui';
import { addToast } from '../../../components/toasts/Toast';
import NotificationWebhookSettings from './NotificationWebhookSettings';

const Styles = styled.div`
  margin: 10px 20px;
`;

const CardContainerStyled = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 24px;
  margin-top: 16px;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 24px -4px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.4);
  }
`;

const SwitchRowStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
  
  .switch-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    
    span {
      font-size: 11.5px;
      color: var(--text-muted);
      display: block;
      margin-top: 3px;
      font-weight: 400;
    }
  }
`;

const InfoCardStyled = styled.div`
  border-left: 4px solid var(--color-info);
  background: rgba(14, 165, 233, 0.03);
  border-radius: 8px;
  padding: 18px;
  font-size: 13.5px;
  color: var(--text-secondary);
  line-height: 1.6;
  border-top: 1px solid rgba(255, 255, 255, 0.02);
  border-right: 1px solid rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
`;

const TabContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  h3 {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 4px 0;
  }

  p {
    font-size: 13.5px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0;
  }
  
  .post-payload-header {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-primary);
    margin-top: 24px;
    margin-bottom: 4px;
  }
`;

const ItemStyles = styled.div`
  margin: 10px 0;
`;

const StatusBadge = styled.span`
  font-size: 8.5px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  margin-left: auto;
  line-height: 1.2;
  display: inline-block;

  &.enabled {
    background: rgba(16, 185, 129, 0.1);
    color: var(--color-success);
    border: 1px solid rgba(16, 185, 129, 0.2);
  }
  
  &.warning {
    background: rgba(245, 158, 11, 0.1);
    color: var(--color-warning);
    border: 1px solid rgba(245, 158, 11, 0.2);
  }
  
  &.configured {
    background: rgba(255, 255, 255, 0.04);
    color: var(--text-secondary);
    border: 1px solid rgba(255, 255, 255, 0.08);
  }

  &.disabled {
    background: rgba(255, 255, 255, 0.02);
    color: var(--text-muted);
    border: 1px solid rgba(255, 255, 255, 0.04);
  }
`;

const TerminalContainer = styled.div`
  background: #141417;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  margin-top: 16px;
  max-width: 550px;
`;

const TerminalHeader = styled.div`
  background: #0f0f11;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .dots {
    display: flex;
    gap: 6px;
    
    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
      
      &.red { background: #ff5f56; }
      &.yellow { background: #ffbd2e; }
      &.green { background: #27c93f; }
    }
  }

  .title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 0 auto 0 -36px;
    flex-grow: 1;
    text-align: center;
    letter-spacing: 0.5px;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  max-width: 550px;
`;

const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  .step-header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    font-size: 14.5px;
    color: var(--text-primary);
    
    .step-number {
      background: rgba(99, 102, 241, 0.1);
      color: var(--color-primary);
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 700;
    }
  }

  .step-body {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.5;
    
    a {
      color: var(--color-info);
      font-weight: 600;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

// Helper brand SVG icons
const icons = {
    settings: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
    ),
    email: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
    ),
    webhook: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6"></polyline>
            <polyline points="8 6 2 12 8 18"></polyline>
        </svg>
    ),
    telegram: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#229ED9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.13 2.82a1.62 1.62 0 0 0-1.57-.15L2.55 9.47a1.13 1.13 0 0 0-.17 2l4.8 1.9L17.2 6.1c.22-.15.47.16.27.34L9.7 13.84v3.83a1 1 0 0 0 1.69.75l2.67-2.3 4.86 3.6a1 1 0 0 0 1.57-.54l3.5-16.1a1 1 0 0 0-.86-1.18z"></path>
        </svg>
    ),
    pushover: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#39a9ed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
    ),
    pushsafer: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2ebd59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
            <line x1="12" y1="18" x2="12.01" y2="18"></line>
        </svg>
    ),
    slack: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#E01E5A' }}>
            <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523 2.528 2.528 0 0 1-2.522-2.523 2.528 2.528 0 0 1 2.522-2.52h2.52v2.52zm1.261 0a2.528 2.528 0 0 1 2.52-2.52h5.043a2.528 2.528 0 0 1 2.522 2.52v5.042a2.528 2.528 0 0 1-2.522 2.52H8.823a2.528 2.528 0 0 1-2.52-2.52v-5.042zM8.823 5.043a2.528 2.528 0 0 1-2.52-2.52 2.528 2.528 0 0 1 2.52-2.522 2.528 2.528 0 0 1 2.52 2.522v2.52h-2.52zm0 1.261a2.528 2.528 0 0 1 2.52 2.52v5.043a2.528 2.528 0 0 1-2.52 2.522H3.78a2.528 2.528 0 0 1-2.522-2.522V8.824a2.528 2.528 0 0 1 2.522-2.52h5.043zm10.135 3.761a2.528 2.528 0 0 1 2.52-2.52 2.528 2.528 0 0 1 2.522 2.52 2.528 2.528 0 0 1-2.522 2.52h-2.52v-2.52zm-1.262 0a2.528 2.528 0 0 1-2.52 2.52h-5.043a2.528 2.528 0 0 1-2.522-2.52V3.78a2.528 2.528 0 0 1 2.522-2.52h5.043a2.528 2.528 0 0 1 2.52 2.52v5.043zm-3.778 10.136a2.528 2.528 0 0 1 2.52 2.52 2.528 2.528 0 0 1-2.52 2.522 2.528 2.528 0 0 1-2.52-2.522v-2.52h2.52zm0-1.262a2.528 2.528 0 0 1-2.52-2.52v-5.043a2.528 2.528 0 0 1 2.52-2.522h5.043a2.528 2.528 0 0 1 2.522 2.522v5.043a2.528 2.528 0 0 1-2.522 2.52h-5.043z"/>
        </svg>
    ),
    discord: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#5865F2' }}>
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.094 13.094 0 0 1-1.873-.894.077.077 0 0 1-.008-.128c.126-.093.252-.19.372-.287a.075.075 0 0 1 .077-.011c3.92 1.793 8.18 1.793 12.061 0a.073.073 0 0 1 .078.009c.12.099.246.195.373.289a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.156-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.156 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.156-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.156 2.418z"/>
        </svg>
    ),
    ntfy: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ff9e0f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            <circle cx="12" cy="8" r="1.5" fill="currentColor"></circle>
        </svg>
    ),
    teams: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#464EB8' }}>
            <path d="M11.95 9.1c0-.98.79-1.77 1.76-1.77s1.77.79 1.77 1.77c0 .97-.79 1.76-1.77 1.76-.97 0-1.76-.79-1.76-1.76zm4.99 3.09c-.87 0-2.83.43-2.83 1.3v.97h5.66v-.97c0-.87-1.96-1.3-2.83-1.3zM5.31 9.1c0-.98.79-1.77 1.77-1.77s1.77.79 1.77 1.77c0 .97-.79 1.76-1.77 1.76-.98 0-1.77-.79-1.77-1.76zm4.95 3.09c-.87 0-2.83.43-2.83 1.3v.97h5.66v-.97c0-.87-1.96-1.3-2.83-1.3zm6.68-6.19c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2zm-6.68 0c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2z"/>
        </svg>
    )
};

const getStatusBadge = (enabled, configured) => {
    if (enabled && configured) {
        return <StatusBadge className="enabled">Active</StatusBadge>;
    } else if (enabled && !configured) {
        return <StatusBadge className="warning">Pending</StatusBadge>;
    } else if (!enabled && configured) {
        return <StatusBadge className="configured">Disabled</StatusBadge>;
    } else {
        return <StatusBadge className="disabled">Inactive</StatusBadge>;
    }
};

const AlertsSection = props => {
    const { user } = props;
    const { notifications, nmetaData } = userStore.user;
    const {
        email,
        telegram,
        webhook,
        pushover,
        pushsafer,
        slack,
        discord,
        ntfy,
        teams
    } = notifications;

    const [isSendingSave, setIsSendingSave] = useState(false);

    const handleChange = useCallback(async (key, data, metaData) => {
        if (isSendingSave) {
            return;
        }

        setIsSendingSave(true);
        try {
            await userStore.updateNotifications(key, data, metaData);
            addToast('Alarms settings changed', 'suc');
        } catch (error) {
            addToast(error.message || 'Failed to change notification settings', 'error');
        } finally {
            setIsSendingSave(false);
        }
    }, [isSendingSave, user]);

    return (
        <Styles>
            <MenuSettingsComponent
                title={ 'Notification Center' }
                menu={ [
                    {
                        title: 'Alarms Settings',
                        icon: icons.settings,
                        categoryHeader: 'Overview'
                    },
                    {
                        title: 'Email',
                        icon: icons.email,
                        categoryHeader: 'Direct Channels',
                        subTitle: getStatusBadge(email, true)
                    },
                    {
                        title: 'Telegram',
                        icon: icons.telegram,
                        subTitle: getStatusBadge(
                            telegram,
                            hasNotificationDestination(nmetaData?.telegram)
                        )
                    },
                    {
                        title: 'Webhook',
                        icon: icons.webhook,
                        categoryHeader: 'Integrations & Webhooks',
                        subTitle: getStatusBadge(webhook, nmetaData && !!nmetaData.webhook)
                    },
                    {
                        title: 'Slack',
                        icon: icons.slack,
                        subTitle: getStatusBadge(slack, nmetaData && !!nmetaData.slack)
                    },
                    {
                        title: 'Discord',
                        icon: icons.discord,
                        subTitle: getStatusBadge(discord, nmetaData && !!nmetaData.discord)
                    },
                    {
                        title: 'ntfy',
                        icon: icons.ntfy,
                        subTitle: getStatusBadge(ntfy, nmetaData && !!nmetaData.ntfy)
                    },
                    {
                        title: 'Microsoft Teams',
                        icon: icons.teams,
                        subTitle: getStatusBadge(teams, nmetaData && !!nmetaData.teams)
                    },
                    {
                        title: 'Pushover',
                        icon: icons.pushover,
                        categoryHeader: 'Push Services',
                        subTitle: getStatusBadge(pushover, nmetaData && !!nmetaData.pushover)
                    },
                    {
                        title: 'Pushsafer',
                        icon: icons.pushsafer,
                        subTitle: getStatusBadge(pushsafer, nmetaData && !!nmetaData.pushsafer)
                    },
                ] }
                content={ [
                    <ItemStyles style={{ margin: 0 }}>
                        <InfoCardStyled>
                            <strong>Settings Notice:</strong> Alarms and thresholds are configured individually per server. Please navigate to any of your monitored servers and open their settings page to customize triggers, cpu/memory spike limits, disk alerts, and response thresholds.
                        </InfoCardStyled>
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <EmailAlert
                            data={ email }
                            handleChange={ handleChange }
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <TelegramAlert
                            data={ telegram }
                            handleChange={ handleChange }
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <Webhook
                            data={ webhook }
                            nmetaData={ nmetaData }
                            handleChange={ handleChange }
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <NotificationWebhookSettings
                            title="Slack"
                            providerKey="slack"
                            data={ slack }
                            metaData={ nmetaData ? nmetaData.slack : '' }
                            handleChange={ handleChange }
                            description="Post alarms directly to a Slack channel using an incoming webhook URL."
                            documentationUrl="https://api.slack.com/messaging/webhooks"
                            fieldLabel="Incoming Webhook URL"
                            placeholder="https://hooks.slack.com/services/..."
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <NotificationWebhookSettings
                            title="Discord"
                            providerKey="discord"
                            data={ discord }
                            metaData={ nmetaData ? nmetaData.discord : '' }
                            handleChange={ handleChange }
                            description="Post alarms directly to a Discord channel using a Discord webhook."
                            documentationUrl="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
                            fieldLabel="Webhook URL"
                            placeholder="https://discord.com/api/webhooks/..."
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <NotificationWebhookSettings
                            title="ntfy"
                            providerKey="ntfy"
                            data={ ntfy }
                            metaData={ nmetaData ? nmetaData.ntfy : '' }
                            handleChange={ handleChange }
                            description="Send alarms as push notifications to an ntfy topic (supports self-hosted ntfy topics)."
                            documentationUrl="https://docs.ntfy.sh/publish/"
                            fieldLabel="Topic URL"
                            placeholder="https://ntfy.sh/my-private-topic"
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <NotificationWebhookSettings
                            title="Microsoft Teams"
                            providerKey="teams"
                            data={ teams }
                            metaData={ nmetaData ? nmetaData.teams : '' }
                            handleChange={ handleChange }
                            description="Post alarm cards directly to a Teams channel using a Teams incoming webhook."
                            documentationUrl="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                            fieldLabel="Webhook URL"
                            placeholder="https://m365x123.webhook.office.com/..."
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <PushoverAlert
                            nmetaData={ nmetaData }
                            data={ pushover }
                            handleChange={ handleChange }
                        />
                    </ItemStyles>,
                    <ItemStyles style={{ margin: 0 }}>
                        <PushSaferAlert
                            nmetaData={ nmetaData }
                            data={ pushsafer }
                            handleChange={ handleChange }
                        />
                    </ItemStyles>
                ] }
            />
        </Styles>
    );
};

export default AlertsSection;

const EmailAlert = ({ data, handleChange }) => {
    return (
        <TabContentStyled>
            <h3>Email Notifications</h3>
            <p>Receive alert notifications from <strong>alarms@syagent.com</strong>.</p>
            
            <InfoCardStyled style={{ borderLeftColor: 'var(--color-warning)', background: 'rgba(245, 158, 11, 0.03)', marginTop: '8px' }}>
                <strong>Important Notice:</strong> Since our emailing service does not use a dedicated IP address, please check your spam folder or add alarms@syagent.com to your address book if you do not receive expected notifications.
            </InfoCardStyled>

            <CardContainerStyled>
                <SwitchRowStyled>
                    <div className="switch-label">
                        Enable Email alerts
                        <span>Send system alerts directly to your registered email.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ data }
                        onChange={ () => handleChange('email', !data) }
                    />
                </SwitchRowStyled>
            </CardContainerStyled>
        </TabContentStyled>
    );
};

const Webhook = ({ data, handleChange, nmetaData }) => {
    const webhook = nmetaData ? nmetaData.webhook : undefined;
    const [webhookUrl, setWebhookUrl] = useState(webhook || '');
    const [toggle, setToggle] = useState(data);

    const handleSubmit = e => {
        e.preventDefault();
        const metaData = {
            webhook: webhookUrl
        };
        handleChange('webhook', toggle, metaData);
    };

    return (
        <TabContentStyled>
            <h3>Webhook Settings</h3>
            <p>Configure a custom HTTP endpoint to receive POST alert payloads from SyAgent.</p>

            <CardContainerStyled>
                <SwitchRowStyled>
                    <div className="switch-label">
                        Enable Webhook alerts
                        <span>POST alert payloads to your server endpoint.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ toggle }
                        onChange={ () => setToggle(!toggle) }
                    />
                </SwitchRowStyled>

                <EuiFormRow label="Webhook URL" fullWidth helpText="We will perform an HTTP POST request to this endpoint with alarm payload.">
                    <EuiFieldText
                        value={ webhookUrl }
                        onChange={ e => setWebhookUrl(e.target.value) }
                        placeholder="https://yourdomain.com/webhook"
                        fullWidth
                    />
                </EuiFormRow>

                <ButtonContainer>
                    <EuiButton
                        isDisabled={ !webhookUrl }
                        size="s"
                        onClick={ handleSubmit }
                        fill
                        style={{ minWidth: '130px', borderRadius: '8px' }}
                    >
                        Save Configuration
                    </EuiButton>
                </ButtonContainer>
            </CardContainerStyled>

            <div className="post-payload-header">POST Payload Example</div>
            <TerminalContainer>
                <TerminalHeader>
                    <div className="dots">
                        <span className="red"></span>
                        <span className="yellow"></span>
                        <span className="green"></span>
                    </div>
                    <div className="title">POST Webhook Payload Example</div>
                </TerminalHeader>
                <EuiCodeBlock language="json" fontSize="s" paddingSize="s" style={{ border: 'none', background: 'transparent' }}>
                    { JSON.stringify({
                            'server_name': 'Production Server',
                            'severity': 'Critical',
                            'message': 'Agent stopped receiving data',
                            'date': new Date().toISOString()
                        },
                        null, 2
                    ) }
                </EuiCodeBlock>
            </TerminalContainer>
        </TabContentStyled>
    );
};

const TelegramAlert = ({ data, handleChange }) => {
    const [linkToken, setLinkToken] = useState('');
    const [isLoadingLink, setIsLoadingLink] = useState(false);
    const [linkError, setLinkError] = useState('');

    const loadLinkToken = useCallback(async () => {
        setIsLoadingLink(true);
        setLinkError('');
        try {
            const response = await fetchService.authenticatedGet('/telegram-link-token');
            const token = response?.data?.data_o?.token;
            if (typeof token !== 'string' || token === '') {
                throw new Error('Invalid Telegram link response');
            }
            setLinkToken(token);
        } catch (error) {
            setLinkToken('');
            setLinkError(error.message || 'Failed to create Telegram link command');
        } finally {
            setIsLoadingLink(false);
        }
    }, []);

    useEffect(() => {
        if (data) {
            loadLinkToken();
        } else {
            setLinkToken('');
            setLinkError('');
        }
    }, [data, loadLinkToken]);

    const commands = telegramLinkCommands(linkToken);

    return (
        <TabContentStyled>
            <h3>Telegram Notifications</h3>
            <p>Receive alert notifications via the SyAgent Telegram bot directly or in group chats.</p>

            <CardContainerStyled>
                <SwitchRowStyled>
                    <div className="switch-label">
                        Enable Telegram alerts
                        <span>Send alert notifications to your Telegram chats.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ data }
                        onChange={ () => handleChange('telegram', !data) }
                    />
                </SwitchRowStyled>
            </CardContainerStyled>

            { data && isLoadingLink && (
                <p style={{ marginTop: '16px' }}>Creating secure Telegram link...</p>
            ) }

            { data && linkError && (
                <div style={{ marginTop: '16px' }}>
                    <p>{ linkError }</p>
                    <EuiButton iconType={ 'refresh' } onClick={ loadLinkToken } size="s">
                        Retry
                    </EuiButton>
                </div>
            ) }

            { data && linkToken && (
                <StepsContainer>
                    <StepCard>
                        <div className="step-header">
                            <span className="step-number">1</span>
                            <span>Direct Telegram Messages</span>
                        </div>
                        <div className="step-body">
                            <p style={{ marginBottom: '12px' }}>
                                Start a chat with our bot: <a href="https://t.me/syagent_bot" target="_blank" rel="noreferrer">Open SyAgent Telegram Bot</a>.
                            </p>
                            <p style={{ marginBottom: '12px' }}>
                                Send your personal identification command to link your chat:
                            </p>
                            <EuiCodeBlock language="text" fontSize="s" paddingSize="s">
                                { commands.direct }
                            </EuiCodeBlock>
                            <div style={{ marginTop: '12px' }}>
                                <EuiCopy textToCopy={ commands.direct }>
                                    { copy => (
                                        <EuiButton iconType={ 'copyClipboard' } onClick={ copy } size="s" style={{ borderRadius: '6px' }}>
                                            Copy Identification Command
                                        </EuiButton>
                                    ) }
                                </EuiCopy>
                            </div>
                        </div>
                    </StepCard>

                    <StepCard>
                        <div className="step-header">
                            <span className="step-number">2</span>
                            <span>Telegram Group Messages</span>
                        </div>
                        <div className="step-body">
                            <p style={{ marginBottom: '12px' }}>
                                Add the bot <strong>@syagent_bot</strong> to your group.
                            </p>
                            <p style={{ marginBottom: '12px' }}>
                                Run the group command in the group chat to link the chat:
                            </p>
                            <EuiCodeBlock language="text" fontSize="s" paddingSize="s">
                                { commands.group }
                            </EuiCodeBlock>
                            <div style={{ marginTop: '12px' }}>
                                <EuiCopy textToCopy={ commands.group }>
                                    { copy => (
                                        <EuiButton iconType={ 'copyClipboard' } onClick={ copy } size="s" style={{ borderRadius: '6px' }}>
                                            Copy Group Command
                                        </EuiButton>
                                    ) }
                                </EuiCopy>
                            </div>
                        </div>
                    </StepCard>

                    <EuiButton iconType={ 'refresh' } onClick={ loadLinkToken } size="s">
                        Create New Link Command
                    </EuiButton>
                </StepsContainer>
            ) }
        </TabContentStyled>
    );
};

const PushoverAlert = ({ data, handleChange, nmetaData }) => {
    const pushover = nmetaData ? nmetaData.pushover : undefined;
    const [userToken, setUserToken] = useState(pushover || '');
    const [toggle, setToggle] = useState(!!data);

    const handleSubmit = e => {
        e.preventDefault();
        const metaData = {
            pushover: userToken
        };
        handleChange('pushover', toggle, metaData);
    };

    return (
        <TabContentStyled>
            <h3>Pushover Notifications</h3>
            <p>Receive instant push notifications via <a href="https://pushover.net/" target="_blank" rel="noreferrer">Pushover</a>.</p>

            <CardContainerStyled>
                <SwitchRowStyled>
                    <div className="switch-label">
                        Enable Pushover alerts
                        <span>Send system alerts to your Pushover devices.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ toggle }
                        onChange={ () => setToggle(!toggle) }
                    />
                </SwitchRowStyled>

                <EuiFormRow label="User Key" fullWidth helpText="Enter your Pushover user or group key.">
                    <EuiFieldText
                        value={ userToken }
                        onChange={ e => setUserToken(e.target.value) }
                        placeholder="Enter your Pushover user/group key"
                        fullWidth
                    />
                </EuiFormRow>

                <ButtonContainer>
                    <EuiButton
                        isDisabled={ !userToken }
                        size="s"
                        onClick={ handleSubmit }
                        fill
                        style={{ minWidth: '130px', borderRadius: '8px' }}
                    >
                        Save Configuration
                    </EuiButton>
                </ButtonContainer>
            </CardContainerStyled>
        </TabContentStyled>
    );
};

const PushSaferAlert = ({ data, handleChange, nmetaData }) => {
    const pushsafer = nmetaData ? nmetaData.pushsafer : undefined;
    const [userToken, setUserToken] = useState(pushsafer || '');
    const [toggle, setToggle] = useState(!!data);

    const handleSubmit = e => {
        e.preventDefault();
        const metaData = {
            pushsafer: userToken
        };
        handleChange('pushsafer', toggle, metaData);
    };

    return (
        <TabContentStyled>
            <h3>Pushsafer Notifications</h3>
            <p>Receive instant push notifications via <a href="https://www.pushsafer.com/" target="_blank" rel="noreferrer">Pushsafer</a>.</p>

            <CardContainerStyled>
                <SwitchRowStyled>
                    <div className="switch-label">
                        Enable Pushsafer alerts
                        <span>Send system alerts to your Pushsafer devices.</span>
                    </div>
                    <EuiSwitch
                        label=""
                        checked={ toggle }
                        onChange={ () => setToggle(!toggle) }
                    />
                </SwitchRowStyled>

                <EuiFormRow label="Private Key" fullWidth helpText="Enter your Pushsafer private key.">
                    <EuiFieldText
                        value={ userToken }
                        onChange={ e => setUserToken(e.target.value) }
                        placeholder="Enter your Pushsafer private key"
                        fullWidth
                    />
                </EuiFormRow>

                <ButtonContainer>
                    <EuiButton
                        isDisabled={ !userToken }
                        size="s"
                        onClick={ handleSubmit }
                        fill
                        style={{ minWidth: '130px', borderRadius: '8px' }}
                    >
                        Save Configuration
                    </EuiButton>
                </ButtonContainer>
            </CardContainerStyled>
        </TabContentStyled>
    );
};
